/* eslint-disable react-hooks/exhaustive-deps */
import * as THREE from "three";
import React, { useMemo, useState } from 'react';
import { useThree } from '@react-three/fiber';

import { const_var, params } from '../../../../../../../redux/reducers/reducer';
import { controls } from '../../../../../../../redux/reducers/common/eventReducer';

import { CSG } from "../../../../../../../assets/libs/csg-lib";
import { extrudeSettings, roofPattern, roofPatternCurve, textureAnisotropy } from '../../../../../../../utils/Functions';
import useMouseEvents from '../../../../../../../hooks/useMouseEvents';
import { MaterialInnerRoof, MaterialOuterRoof, MaterialRoofRidge } from '../../../../../CustomMaterial';

const RoofModel = () => {
    const { gl } = useThree();

    const [overHangWidth, setOverHangWidth] = useState(0);
    const [overHangLength, setOverHangLength] = useState(0);
    const [patternLength, setPatternLength] = useState(0);
    const [patternDepth, setPatternDepth] = useState(params.leanF_p_d);

    const canvasElement = document.getElementsByClassName("buildingcanvas")
    const isPattern = (canvasElement != undefined && canvasElement[0]?.clientWidth > 1050) ? true : false
    
    const roofThickness = 0.005;
    const ridgeThickness = 0.022;

    const sectionW = params.leanF_p_w + 0.05;
    const roofPitchHeight = (params.b_l_t_r_pF / 12) * sectionW / 2;
    const roofAngle = Math.atan((roofPitchHeight) / (sectionW / 2));
    const roofCutShapeWidth = Math.sqrt(Math.pow(sectionW, 2) * 2)
    const leanRoofWidth = Math.sqrt(Math.pow((sectionW / 2), 2) + Math.pow(roofPitchHeight, 2));
    const diagonalLength = overHangWidth + leanRoofWidth;
    
    let regularHeight;
    if(params.p_r_s === 1) regularHeight = const_var.roofRegularDst * 2;
    else regularHeight = 0;
    const texture_metalSprite = const_var.metalTexture;

    const verticalMap = texture_metalSprite.clone();
    textureAnisotropy(gl, verticalMap, 1.3, 1.3, Math.PI);
    verticalMap.needsUpdate = true;
    const horizontalMap = texture_metalSprite.clone();
    textureAnisotropy(gl, horizontalMap, 1.3, 1.3, Math.PI / 2);
    horizontalMap.offset.setY(params.o_v_h_n_g_e ? 0.5 : 0.15)
    horizontalMap.needsUpdate = true;

    const roofTexture = useMemo(() => {
        if (params.p_r_s === 1 || params.p_r_s === 2) {
            return verticalMap;
        } else if (params.p_r_s === 3) {
            return horizontalMap;
        }
    }, [horizontalMap, params.p_r_s, verticalMap]);

    let sideOverHangSize = 0 
    let endOverHangSize = 0 
    if(const_var.isOverhang['side'] !== undefined && const_var.isOverhang['side'].size){
        sideOverHangSize = Number(const_var.isOverhang['side'].size)
        if(sideOverHangSize<=1){
            sideOverHangSize = (0.25 + sideOverHangSize)
        }
    }
    if(const_var.isOverhang['end'] !== undefined && const_var.isOverhang['end'].size){
        endOverHangSize = Number(const_var.isOverhang['end'].size)
    }
  
    const roofPanModel = useMemo(() => {
        // This model is used only when the roofStyle is not "Regular"
        const model = new THREE.Shape();
        model.moveTo(-(params.leanF_p_d / 2 + overHangLength), overHangWidth);
        model.lineTo((params.leanF_p_d / 2), 0 + overHangWidth);
        model.lineTo((params.leanF_p_d / 2), -leanRoofWidth);
        model.lineTo(-(params.leanF_p_d / 2 + overHangLength), -leanRoofWidth);

        return model;
    }, [params.leanF_p_d, overHangLength, params.p_w, overHangWidth, leanRoofWidth, params.leanF_p_w, params.b_l_t_r_pF]);

    const roofRegularRoofInnerModel = useMemo(() => {
        const pitchHeightOffset = const_var.roofRegularDst * (params.b_l_t_r_pF / 12);
        const roofSpace = 0.025;

        const model = new THREE.Shape();
        model.moveTo(-sectionW / 2 + roofSpace, -0.01);
        model.lineTo(-sectionW / 2 + roofSpace, const_var.roofRegularDst);
        model.quadraticCurveTo( -sectionW / 2 + roofSpace, const_var.roofRegularDst * 1.8 + pitchHeightOffset / 10 - roofSpace,
                                -sectionW / 2 + roofSpace + const_var.roofRegularDst, const_var.roofRegularDst * 2 + const_var.roofRegularDst * params.b_l_t_r_pF / 12 - roofSpace);
        model.lineTo(0, (params.b_l_t_r_pF / 12) * sectionW / 2 + const_var.roofRegularDst * 2 - roofSpace);
        model.lineTo(sectionW / 2 - roofSpace - const_var.roofRegularDst, const_var.roofRegularDst * 2 + const_var.roofRegularDst * params.b_l_t_r_pF / 12 - roofSpace);
        model.quadraticCurveTo( sectionW / 2 - roofSpace, const_var.roofRegularDst * 1.8 + pitchHeightOffset / 10 - roofSpace,
                                sectionW / 2 - roofSpace, const_var.roofRegularDst);
        model.lineTo(sectionW / 2 - roofSpace, -0.01);

        model.lineTo(sectionW / 2 - roofSpace - roofThickness, -0.01);

        model.lineTo(sectionW / 2 - roofSpace - roofThickness, const_var.roofRegularDst - roofSpace);
        model.quadraticCurveTo( sectionW / 2 - roofSpace - roofThickness, const_var.roofRegularDst * 1.8 + pitchHeightOffset / 10 - roofSpace - roofThickness,
                                sectionW / 2 - roofSpace - const_var.roofRegularDst - roofThickness, const_var.roofRegularDst * 2 + const_var.roofRegularDst * params.b_l_t_r_pF / 12 - roofSpace - roofThickness);
        model.lineTo(0, (params.b_l_t_r_pF / 12) * sectionW / 2 + const_var.roofRegularDst * 2 - roofSpace - roofThickness);
        model.lineTo(-(sectionW / 2 - roofSpace - const_var.roofRegularDst - roofThickness), const_var.roofRegularDst * 2 + const_var.roofRegularDst * params.b_l_t_r_pF / 12 - roofSpace - roofThickness)
        model.quadraticCurveTo( -(sectionW / 2 - roofSpace - roofThickness), const_var.roofRegularDst * 1.8 + pitchHeightOffset / 10 - roofSpace - roofThickness,
                                -(sectionW / 2 - roofSpace - roofThickness), const_var.roofRegularDst - roofSpace)
        model.lineTo(-(sectionW / 2 - roofSpace - roofThickness), -0.01);
        model.closePath();

        return model;
    }, [params.leanF_p_w, params.b_l_t_r_pF])

    const topRidgeModel = useMemo(() => {
        const ridgeWidth = 0.25;
        
        const model = new THREE.Shape();
        model.moveTo(-ridgeWidth, (params.b_l_t_r_pF / 12) * (params.leanF_p_w / 2 - ridgeWidth));
        model.lineTo(0, (params.b_l_t_r_pF / 12) * params.leanF_p_w / 2 + regularHeight);
        model.lineTo(ridgeWidth, (params.b_l_t_r_pF / 12) * (params.leanF_p_w / 2 - ridgeWidth));
        model.lineTo(ridgeWidth, (params.b_l_t_r_pF / 12) * (params.leanF_p_w / 2 - ridgeWidth) + (isPattern ? ridgeThickness * 3 : ridgeThickness));
        model.lineTo(0, (params.b_l_t_r_pF / 12) * params.leanF_p_w / 2 + regularHeight + (isPattern ? ridgeThickness * 3 : ridgeThickness));
        model.lineTo(-ridgeWidth, (params.b_l_t_r_pF / 12) * (params.leanF_p_w / 2 - ridgeWidth) + (isPattern ? ridgeThickness * 3 : ridgeThickness));
        model.closePath();

        return model;
    }, [params.leanF_p_w, params.b_l_t_r_pF, regularHeight, isPattern]);

    const roofModelObj = useMemo(() => {
        let mainLeftOuter, mainRightOuter, mainLeftInner, mainRightInner, curveLeftOuter, curveRightOuter, innerRegularRoof, topRidge;

        const centerRoofDelModel = new THREE.Shape();
        centerRoofDelModel.moveTo(-params.p_w / 2, 0);
        centerRoofDelModel.lineTo(-params.p_w / 2, params.p_h);
        centerRoofDelModel.lineTo(0, params.p_h + params.p_w / 2 * params.p_r_p / 12);
        centerRoofDelModel.lineTo(params.p_w / 2, params.p_h);
        centerRoofDelModel.lineTo(params.p_w / 2, 0);
        centerRoofDelModel.closePath();

        const outerRoofMesh = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(patternLength, isPattern, "roof"), extrudeSettings(patternDepth)));
        const innerRoofMesh = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPanModel, extrudeSettings(roofThickness)));
        const innerRegularRoofMesh = new THREE.Mesh(new THREE.ExtrudeGeometry(roofRegularRoofInnerModel, extrudeSettings(patternDepth)));
        const curveOuterMesh = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPatternCurve(1, params.b_l_t_r_pF, isPattern), extrudeSettings(patternDepth)));
        const centerRoofDelMesh = new THREE.Mesh(new THREE.ExtrudeGeometry(centerRoofDelModel, extrudeSettings(params.p_d + 0.135)));
        const topRidgeModelMesh = new THREE.Mesh(new THREE.ExtrudeGeometry(topRidgeModel, extrudeSettings(patternLength)));

        let leftOuterRoofMeshPosUpdate = outerRoofMesh.clone();
        leftOuterRoofMeshPosUpdate.rotation.set(0, (params.p_r_s === 3 ? -Math.PI / 2 : Math.PI), 0)
        leftOuterRoofMeshPosUpdate.updateMatrixWorld();

        let tempGroup1 = new THREE.Object3D();
        tempGroup1.position.set(0, params.leanF_p_h + roofPitchHeight + regularHeight + (params.p_r_s === 1 ? 0 : 0.02), (params.p_r_s === 3 ? params.p_d / 2 : params.p_d / 2 + patternDepth));
        tempGroup1.rotation.set(0, 0, roofAngle);
        tempGroup1.updateMatrixWorld();
        leftOuterRoofMeshPosUpdate.applyMatrix4(tempGroup1.matrixWorld);
        leftOuterRoofMeshPosUpdate.updateMatrixWorld();
        
        let rightOuterRoofMeshPosUpdate = outerRoofMesh.clone();
        rightOuterRoofMeshPosUpdate.rotation.set(0, (params.p_r_s === 3 ? Math.PI / 2 : 0), 0)
        rightOuterRoofMeshPosUpdate.updateMatrixWorld();

        let tempGroup2 = new THREE.Object3D();
        tempGroup2.position.set( 0, params.leanF_p_h + roofPitchHeight + regularHeight + (params.p_r_s === 1 ? 0 : 0.02), (params.p_r_s === 3 ? params.p_d / 2 + params.leanF_p_d + overHangLength : params.p_d / 2));
        tempGroup2.rotation.set(0, 0, -roofAngle);
        tempGroup2.updateMatrixWorld();
        rightOuterRoofMeshPosUpdate.applyMatrix4(tempGroup2.matrixWorld);
        rightOuterRoofMeshPosUpdate.updateMatrixWorld();

        let leftInnerRoofMeshPosUpdate = innerRoofMesh.clone();
        leftInnerRoofMeshPosUpdate.position.set(-params.leanF_p_w / 2 - 0.018, params.leanF_p_h + 0.01 - roofThickness, params.p_d / 2 + params.leanF_p_d / 2);
        leftInnerRoofMeshPosUpdate.rotation.set(0, Math.PI / 2, 0);
        leftInnerRoofMeshPosUpdate.rotateOnAxis(new THREE.Vector3(1, 0, 0), -Math.PI / 2 - roofAngle);
        leftInnerRoofMeshPosUpdate.updateMatrixWorld();

        let rightInnerRoofMeshPosUpdate = innerRoofMesh.clone();
        rightInnerRoofMeshPosUpdate.position.set(params.leanF_p_w / 2 + 0.018, params.leanF_p_h + 0.01, params.p_d / 2 + params.leanF_p_d / 2);
        rightInnerRoofMeshPosUpdate.rotation.set(0, Math.PI / 2, 0);
        rightInnerRoofMeshPosUpdate.rotateOnAxis(new THREE.Vector3(1, 0, 0), Math.PI / 2 + roofAngle);
        rightInnerRoofMeshPosUpdate.updateMatrixWorld();

        let curveLeftOuterMeshPosUpdate = curveOuterMesh.clone();
        curveLeftOuterMeshPosUpdate.position.set(-params.leanF_p_w / 2 - 0.02, params.leanF_p_h, params.p_d / 2);
        curveLeftOuterMeshPosUpdate.rotation.set(0, 0, 0);
        curveLeftOuterMeshPosUpdate.updateMatrixWorld();

        let curveRightOuterMeshPosUpdate = curveOuterMesh.clone();
        curveRightOuterMeshPosUpdate.position.set(params.leanF_p_w / 2 + 0.02, params.leanF_p_h, params.p_d / 2 + patternDepth);
        curveRightOuterMeshPosUpdate.rotation.set(0, Math.PI, 0);
        curveRightOuterMeshPosUpdate.updateMatrixWorld();

        let centerRoofDelMesh_Temp = centerRoofDelMesh.clone();
        centerRoofDelMesh_Temp.position.set(0, 0.012, -params.p_d / 2);
        centerRoofDelMesh_Temp.updateMatrixWorld();

        let topRidgeModelMesh_Temp = topRidgeModelMesh.clone();
        topRidgeModelMesh_Temp.position.set(0, params.leanF_p_h + 0.02 + ridgeThickness, (params.p_d / 2));
        topRidgeModelMesh_Temp.updateMatrixWorld();

        let innerRegularRoofMeshPosUpdate = innerRegularRoofMesh.clone();
        innerRegularRoofMeshPosUpdate.position.set(0, params.leanF_p_h, params.p_d / 2);
        innerRegularRoofMeshPosUpdate.rotation.set(0, 0, 0);
        innerRegularRoofMeshPosUpdate.updateMatrixWorld();

        const leftOuterRoofMesh_CSG = CSG.fromMesh(leftOuterRoofMeshPosUpdate);
        const rightOuterRoofMesh_CSG = CSG.fromMesh(rightOuterRoofMeshPosUpdate);
        const leftInnerRoofMesh_CSG = CSG.fromMesh(leftInnerRoofMeshPosUpdate);
        const rightInnerRoofMesh_CSG = CSG.fromMesh(rightInnerRoofMeshPosUpdate);
        const curveLeftOuterMesh_CSG = CSG.fromMesh(curveLeftOuterMeshPosUpdate);
        const curveRightOuterMesh_CSG = CSG.fromMesh(curveRightOuterMeshPosUpdate);
        const centerRoofDelMesh_CSG = CSG.fromMesh(centerRoofDelMesh_Temp);
        const topRidgeModelMesh_CSG = CSG.fromMesh(topRidgeModelMesh_Temp);
        const innerRegularRoofMesh_CSG = CSG.fromMesh(innerRegularRoofMeshPosUpdate);

        const roofLeftOuterMeshCSG = leftOuterRoofMesh_CSG.subtract(centerRoofDelMesh_CSG);
        const roofRightOuterMeshCSG = rightOuterRoofMesh_CSG.subtract(centerRoofDelMesh_CSG);
        const roofLeftInnerMeshCSG = leftInnerRoofMesh_CSG.subtract(centerRoofDelMesh_CSG);
        const roofRightInnerMeshCSG = rightInnerRoofMesh_CSG.subtract(centerRoofDelMesh_CSG);
        const topRidgeModelMeshCSG = topRidgeModelMesh_CSG.subtract(centerRoofDelMesh_CSG);
        const curveLeftOuterMeshCSG = curveLeftOuterMesh_CSG.subtract(centerRoofDelMesh_CSG);
        const curveRightOuterMeshCSG = curveRightOuterMesh_CSG.subtract(centerRoofDelMesh_CSG);
        const innerRegularRoofMeshCSG = innerRegularRoofMesh_CSG.subtract(centerRoofDelMesh_CSG);

        const roofLeftOuterMeshFinal = CSG.toMesh(roofLeftOuterMeshCSG,new THREE.Matrix4());
        const roofRightOuterMeshFinal = CSG.toMesh(roofRightOuterMeshCSG,new THREE.Matrix4());
        const roofLeftInnerMeshFinal = CSG.toMesh(roofLeftInnerMeshCSG, new THREE.Matrix4());
        const roofRightInnerMeshFinal = CSG.toMesh(roofRightInnerMeshCSG, new THREE.Matrix4());
        const curveLeftOuterMeshFinal = CSG.toMesh(curveLeftOuterMeshCSG, new THREE.Matrix4());
        const curveRightOuterMeshFinal = CSG.toMesh(curveRightOuterMeshCSG, new THREE.Matrix4());
        const CSGTopRidgeModel = CSG.toMesh(topRidgeModelMeshCSG, new THREE.Matrix4());
        const innerRegularRoofMeshFinal = CSG.toMesh(innerRegularRoofMeshCSG, new THREE.Matrix4());
            
        mainLeftOuter = roofLeftOuterMeshFinal.geometry;
        mainRightOuter = roofRightOuterMeshFinal.geometry;
        mainLeftInner = roofLeftInnerMeshFinal.geometry;
        mainRightInner = roofRightInnerMeshFinal.geometry;
        curveLeftOuter = curveLeftOuterMeshFinal.geometry;
        curveRightOuter = curveRightOuterMeshFinal.geometry;
        innerRegularRoof = innerRegularRoofMeshFinal.geometry;
        topRidge = CSGTopRidgeModel.geometry;

        return {mainLeftOuter, mainRightOuter, mainLeftInner, mainRightInner, curveLeftOuter, curveRightOuter, innerRegularRoof, topRidge};
    }, [params.leanF_p_h, params.leanF_p_w, params.leanF_p_d, roofPitchHeight, params.b_l_t_r_pF, roofCutShapeWidth, patternLength, patternDepth, params.p_w, params.p_h, params.p_d, params.lean_p_pos, overHangWidth, params.p_r_s, roofAngle, params.o_v_h_n_g, params.o_v_h_n_g_e, params.p_r_p, sideOverHangSize, endOverHangSize, isPattern])

    useMemo(() => {
        if (params.p_r_s === 1 || params.p_r_s === 2) {
            if(params.p_r_s === 1) {
                const sectionW = (params.leanF_p_w + 0.1) / 2 - const_var.roofRegularDst;
                setPatternLength(Math.sqrt(Math.pow(sectionW, 2) + Math.pow(((params.b_l_t_r_pF / 12) * sectionW), 2)));
                setPatternDepth(params.leanF_p_d + 0.45)
            } else {
                if (params.o_v_h_n_g === true){
                    setOverHangWidth(sideOverHangSize)
                } else {
                    setOverHangWidth(0.75);
                }
                if (params.o_v_h_n_g_e === true) {
                    setOverHangLength(endOverHangSize)
                } else {
                    setOverHangLength(0.75)
                }
                setPatternLength(diagonalLength)
                setPatternDepth(params.leanF_p_d + overHangLength)
            }
        } else if (params.p_r_s === 3) {
            if (params.o_v_h_n_g === true){
                setOverHangWidth(sideOverHangSize)
            } else {
                setOverHangWidth(0.75);
            }
            if (params.o_v_h_n_g_e === true) {
                setOverHangLength(endOverHangSize)
            } else {
                setOverHangLength(0.75)
            }
            setPatternLength(params.leanF_p_d + overHangLength)
            setPatternDepth(diagonalLength)
          }
    }, [params.p_r_s, params.leanF_p_d, params.leanF_p_w, params.p_w, params.o_v_h_n_g_e, params.o_v_h_n_g, overHangLength, overHangWidth, diagonalLength, sideOverHangSize, endOverHangSize])

    const handleMouseEvent = (e) => {
        if (controls.isCheckActiveTab === 'doorwindow' && (const_var.focusedSection === "leftlean" && params.activeWall === "L_L_F_S_W")) {
            switch (e.type) {
                case 'pointerdown':
                case 'click':
                case 'pointermove':
                    e.stopPropagation();
                    break;
                default :
                    break
            }
        }
    };
    const eventHandlers = useMouseEvents(handleMouseEvent);
        
    const texture = useMemo(() => {
        let texture = (const_var.wallTextureObj["vertical"] != undefined) ? const_var.wallTextureObj["vertical"].clone() : const_var.wallTextureObj["vertical"];
        textureAnisotropy(gl, texture, 1.3, 1.3, Math.PI/-2);    
        return texture
    }, [params.p_v_w,params.wallTexture])

    return (
        <group {...eventHandlers}>
            <>
                <mesh name="roof-out-left" position={[0, 0.035, 0]} geometry={roofModelObj?.mainLeftOuter} renderOrder={1}>
                    <MaterialOuterRoof isPattern={isPattern} texture={texture} visibleSection={"gableFrontLean"} />
                </mesh>
                <mesh name="roof-out-right" position={[0, 0.035, 0]} geometry={roofModelObj?.mainRightOuter} renderOrder={1}>
                    <MaterialOuterRoof isPattern={isPattern} texture={texture} visibleSection={"gableFrontLean"} />
                </mesh>
                {params.p_r_s === 1 ?
                    <mesh name="roof-in" geometry={roofModelObj?.innerRegularRoof}>
                        <MaterialInnerRoof texture={roofTexture} visibleSection={'gableRightLean'} />
                    </mesh>
                    :
                    <>
                        <mesh name="roof-in-left" geometry={roofModelObj?.mainLeftInner}>
                            <MaterialInnerRoof texture={roofTexture} visibleSection={'gableFrontLean'} />
                        </mesh>
                        <mesh name="roof-in-right" geometry={roofModelObj?.mainRightInner}>
                            <MaterialInnerRoof texture={roofTexture} visibleSection={'gableFrontLean'} />
                        </mesh>
                    </>
                }
            </>

            {params.p_r_s === 1 &&
                <>
                    <mesh name="roof-left-out-curve" position={[0, 0.03, 0]} geometry={roofModelObj?.curveLeftOuter} renderOrder={1}>
                        <MaterialOuterRoof isPattern={isPattern} texture={texture} visibleSection={"gableFrontLean"} />
                    </mesh>
                    <mesh name="roof-right-out-curve" position={[0, 0.03, 0]} geometry={roofModelObj?.curveRightOuter} renderOrder={1}>
                        <MaterialOuterRoof isPattern={isPattern} texture={texture} visibleSection={"gableFrontLean"} />
                    </mesh>
                </>
            }
            {params.p_r_s === 3 &&
                <mesh name="top-ridge-model" position={[0, 0.03, 0]} geometry={roofModelObj?.topRidge}>
                    <MaterialRoofRidge isPattern={isPattern} visibleSection={'gableFrontLean'} />
                </mesh>
            }
        </group>
    )
}

export default RoofModel